import { useEffect, useState } from "react";
import {
  Eye, EyeOff, Lock, Unlock, ChevronUp, ChevronDown, ChevronRight, Trash2, Type, Film, Shapes, Copy, MoreHorizontal, FolderOpen,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { t } from "../../i18n";

import { groupColorMap, groupSelId } from "../../lib/groups";

export { groupColorMap };

const TypeIcon = ({ tipo }) =>
  tipo === "texto" ? <Type className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
    : tipo === "forma" ? <Shapes className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      : <Film className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />;

export function LayersPanel({ elements, selectedId, onSelect, onChange, onReorder, onReorderGroup, onDelete, onDuplicate, multiIds = [], onToggleMulti }) {
  const [editing, setEditing] = useState(null);
  const [openGroups, setOpenGroups] = useState({}); // grupos começam recolhidos
  const ordered = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  const colors = groupColorMap(elements);

  // Ao selecionar uma camada (ex.: clicando no canvas), abre o grupo dela.
  useEffect(() => {
    const g = elements.find((e) => e.id === selectedId)?.grupo;
    if (g) setOpenGroups((o) => (o[g] ? o : { ...o, [g]: true }));
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Linhas: camadas soltas e grupos (cada grupo aparece na posição da sua camada mais acima).
  const rows = [];
  const seen = new Set();
  for (const el of ordered) {
    if (!el.grupo) rows.push({ kind: "layer", el });
    else if (!seen.has(el.grupo)) {
      seen.add(el.grupo);
      rows.push({ kind: "group", name: el.grupo, members: ordered.filter((x) => x.grupo === el.grupo) });
    }
  }

  const toggleGroup = (name) => setOpenGroups((o) => ({ ...o, [name]: !o[name] }));

  const renderLayer = (el, color) => (
    <div
      key={el.id}
      onMouseDown={(e) => (e.shiftKey ? onToggleMulti?.(el.id) : onSelect(el.id))}
      className={`group/layer flex items-center gap-1.5 pr-1.5 py-2 border-b border-border/40 cursor-pointer transition-colors duration-150 ${
        el.id === selectedId || multiIds.includes(el.id) ? "bg-primary/15" : "hover:bg-secondary/60"
      } ${color ? "pl-6" : "pl-2"}`}
      style={color ? { boxShadow: `inset 3px 0 0 ${color}` } : undefined}
      data-testid={`layer-item-${el.id}`}
    >
      <TypeIcon tipo={el.tipo} />
      {editing === el.id ? (
        <input
          autoFocus
          defaultValue={el.nome}
          onBlur={(e) => { onChange(el.id, { nome: e.target.value }, { sendWs: false }); setEditing(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          className="flex-1 bg-transparent border border-primary rounded-sm px-1 text-xs outline-none min-w-0"
          data-testid={`layer-rename-input-${el.id}`}
        />
      ) : (
        <span className={`flex-1 text-xs truncate ${el.visivel === false ? "text-muted-foreground line-through decoration-muted-foreground/50" : ""}`} onDoubleClick={() => setEditing(el.id)}>{el.nome}</span>
      )}
      {/* Estado atual (só indicação; as ações ficam no menu) */}
      {el.travado && <Lock className="h-3 w-3 text-amber-400 flex-shrink-0" aria-label={t("Bloqueada")} />}
      {el.visivel === false && <EyeOff className="h-3 w-3 text-muted-foreground flex-shrink-0" aria-label={t("Oculta")} />}
      <button onClick={(e) => { e.stopPropagation(); onReorder(el.id, "up"); }} className="p-0.5 text-muted-foreground hover:text-foreground" title={t("Subir camada")} data-testid={`layer-up-${el.id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
      <button onClick={(e) => { e.stopPropagation(); onReorder(el.id, "down"); }} className="p-0.5 text-muted-foreground hover:text-foreground" title={t("Descer camada")} data-testid={`layer-down-${el.id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            className="p-0.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary"
            title={t("Mais ações")}
            data-testid={`layer-menu-${el.id}`}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DropdownMenuItem onClick={() => onChange(el.id, { travado: !el.travado }, { sendWs: false })} data-testid={`layer-lock-${el.id}`}>
            {el.travado ? <Unlock className="h-3.5 w-3.5 mr-2" /> : <Lock className="h-3.5 w-3.5 mr-2" />}
            {el.travado ? t("Desbloquear") : t("Bloquear")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onChange(el.id, { visivel: el.visivel === false })} data-testid={`layer-visible-${el.id}`}>
            {el.visivel === false ? <Eye className="h-3.5 w-3.5 mr-2" /> : <EyeOff className="h-3.5 w-3.5 mr-2" />}
            {el.visivel === false ? t("Mostrar") : t("Ocultar")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDuplicate?.(el.id)} data-testid={`layer-duplicate-${el.id}`}>
            <Copy className="h-3.5 w-3.5 mr-2" /> {t("Duplicar")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onDelete(el.id)} className="text-destructive focus:text-destructive" data-testid={`layer-delete-${el.id}`}>
            <Trash2 className="h-3.5 w-3.5 mr-2" /> {t("Excluir")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 text-[10px] font-mono tracking-[0.2em] text-muted-foreground border-b border-border/60">
        {t("CAMADAS")} ({elements.length})
      </div>
      <div className="flex-1 overflow-y-auto">
        {ordered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">{t("Sem elementos.")}</p>}
        {rows.map((r) => {
          if (r.kind === "layer") return renderLayer(r.el);
          const color = colors[r.name];
          const open = !!openGroups[r.name];
          const hasSelected = r.members.some((m) => m.id === selectedId);
          const groupSelected = selectedId === groupSelId(r.name);
          return (
            <div key={`g-${r.name}`} data-testid={`layer-group-${r.name}`}>
              <div
                className={`w-full flex items-center gap-1.5 pl-1 pr-2 border-b border-border/40 transition-colors ${groupSelected ? "bg-primary/15" : hasSelected && !open ? "bg-primary/10" : "hover:bg-secondary/60"}`}
                style={{ boxShadow: `inset 3px 0 0 ${color}`, background: !groupSelected && open ? `${color}14` : undefined }}
              >
                <button
                  onClick={() => toggleGroup(r.name)}
                  aria-expanded={open}
                  className="p-1 rounded-sm hover:bg-secondary flex-shrink-0"
                  title={open ? t("Recolher grupo") : t("Expandir grupo")}
                  data-testid={`layer-group-toggle-${r.name}`}
                >
                  {open ? <ChevronDown className="h-3.5 w-3.5" style={{ color }} /> : <ChevronRight className="h-3.5 w-3.5" style={{ color }} />}
                </button>
                <button
                  onClick={() => { onSelect(groupSelId(r.name)); }}
                  className="flex-1 min-w-0 flex items-center gap-1.5 py-2 text-left"
                  title={t("Selecionar o grupo")}
                  data-testid={`layer-group-select-${r.name}`}
                >
                  <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" style={{ color }} />
                  <span className="flex-1 text-xs font-semibold truncate" style={{ color }}>{r.name}</span>
                  <span className="text-[10px] font-mono rounded-sm px-1.5 py-0.5" style={{ color, background: `${color}22` }}>{r.members.length}</span>
                </button>
                {/* Ordem do grupo: move o grupo todo sobre o vizinho (as camadas acompanham) */}
                <button onClick={(e) => { e.stopPropagation(); onReorderGroup?.(r.name, "up"); }} className="p-0.5 text-muted-foreground hover:text-foreground" title={t("Subir grupo")} data-testid={`layer-group-up-${r.name}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); onReorderGroup?.(r.name, "down"); }} className="p-0.5 text-muted-foreground hover:text-foreground" title={t("Descer grupo")} data-testid={`layer-group-down-${r.name}`}><ChevronDown className="h-3.5 w-3.5" /></button>
              </div>
              {open && r.members.map((m) => renderLayer(m, color))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
